import React from 'react';
import { FaSpinner } from 'react-icons/fa';

/**
 * Componente Button reutilizable
 * 
 * @param {Object} props - Propiedades del componente
 * @param {ReactNode} props.children - Contenido del botón
 * @param {string} props.variant - Variante del botón ('primary', 'secondary', 'success', 'danger', 'warning')
 * @param {string} props.size - Tamaño del botón ('sm', 'md', 'lg')
 * @param {boolean} props.fullWidth - Si el botón debe ocupar todo el ancho disponible
 * @param {function} props.onClick - Función a ejecutar al hacer clic
 * @param {string} props.type - Tipo de botón ('button', 'submit', 'reset')
 * @param {boolean} props.disabled - Si el botón está deshabilitado
 * @param {boolean} props.loading - Si el botón está en estado de carga
 * @param {string} props.className - Clases adicionales
 * @param {boolean} props.outline - Si el botón debe tener estilo de contorno
 * @param {ReactNode} props.icon - Icono opcional para mostrar antes del texto
 * @returns {JSX.Element} - Componente Button
 */
const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  onClick,
  type = 'button',
  disabled = false,
  loading = false,
  className = '',
  icon = null,
  ...rest
}) => {
  // Base styles for all buttons
  const baseStyles = "font-medium rounded-lg transition-all duration-200 flex items-center justify-center shadow-sm";
  
  // Variant styles
  const variantStyles = {
    primary: "bg-blue-600 hover:bg-blue-700 text-white border border-blue-700 shadow-blue-900/20",
    secondary: "bg-gray-600 hover:bg-gray-700 text-white border border-gray-700 shadow-gray-900/20",
    success: "bg-green-600 hover:bg-green-700 text-white border border-green-700 shadow-green-900/20",
    danger: "bg-red-600 hover:bg-red-700 text-white border border-red-700 shadow-red-900/20",
    warning: "bg-yellow-600 hover:bg-yellow-700 text-white border border-yellow-700 shadow-yellow-900/20",
  };
  
  // Size styles
  const sizeStyles = {
    sm: "text-xs py-1 px-2",
    md: "text-sm py-2 px-4",
    lg: "text-base py-3 px-6",
  };
  
  // Hover effect
  const hoverEffect = disabled 
    ? "" 
    : "hover:translate-y-[-1px] hover:shadow focus:ring-2 focus:ring-opacity-50 active:translate-y-[0px]";
  
  // Disabled style (includes loading state)
  const disabledStyle = (disabled || loading) ? "opacity-50 cursor-not-allowed" : "cursor-pointer";
  
  // Width style
  const widthStyle = fullWidth ? "w-full" : "";
  
  const buttonClasses = `
    ${baseStyles}
    ${variantStyles[variant] || variantStyles.primary}
    ${sizeStyles[size] || sizeStyles.md}
    ${hoverEffect}
    ${disabledStyle}
    ${widthStyle}
    ${className}
  `.trim();
  
  return (
    <button
      type={type}
      className={buttonClasses}
      onClick={onClick}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? (
        <>
          <FaSpinner className="animate-spin -ml-1 mr-2 h-4 w-4" />
          {children}
        </>
      ) : (
        <>
          {icon && <span className="mr-2">{icon}</span>}
          {children}
        </>
      )}
    </button>
  );
};

export default Button;